import React, { useState, useEffect, useMemo, useRef } from "react";
import moment from "moment";
import { v4 as uuidv4 } from "uuid";
import { connect } from "react-redux";
import * as reportsRedux from "../reports/redux/reportsRedux";
import * as authRedux from "../../../modules/Auth/_redux/authRedux";
import { get, post } from "../../networking/RequestService";
import { silentlyParseJSON } from "../../app/functions/util";
import DepartmentFilter from "./components/DepartmentFilter";
import EmployeeFilter from "./components/EmployeeFilter";
import SaveFilter from "./components/SaveFilter";
import SimpleModal from "../modal/simpleModal";
import { toast } from "react-toastify";
import CustomDrawer, { DrawerBody, DrawerContent, DrawerHeader } from "app/popleads/components/drawer/CustomDrawer";
import useDisclosure from "app/popleads/hooks/useDisclosure";
import { TooltipOverlay } from "app/popleads/components/TooltipOverlay";
import DateFilter from "./components/DateFilter";
import styles from "../../../popleads/assets/css/design_3.0/CustomDatePicker.module.scss";
import { isSalesFullPageLoading } from "../../app/functions/util";
import InquiryTypeFilter from "./components/InquiryTypeFilter";
import FranchiseFilter from "./components/FranchiseFilter";
import ButtonWithText from "app/popleads/components/ui-kit/Button";

const SalesFilters = ({
    user,
    employees,
    departments,
    dates,
    inquiryTypes,
    franchises,
    countries,
    accounts,
    industries,
    portfolios,
    filterType,
    ...props
}) => {
    const [isLoading, setIsLoading] = useState(false);
    const [usersFilters, setUsersFilters] = useState([]);
    const [selectedFilterId, setSelectedFilterId] = useState(null);
    const [isFilterApplied, setIsFilterApplied] = useState(false);
    const [deletedFilterId, setDeletedFilterId] = useState(null);

    const { isOpen, onClose, onToggle } = useDisclosure();

    const onFilterSelected = (filter) => {
        const { id, filters } = filter;

        setSelectedFilterId(id);

        props.selectEmployee(filters.employees || []);
        props.selectDepartment(filters.departments || []);
        props.selectInquiryType(filters.inquiryTypes || []);
        props.selectFranchise(filters.franchises || []);
        props.selectCountry_Aside(filters.countries || []);
        props.selectAccount_Aside(filters.accounts || []);
        props.selectIndustry_Aside(filters.industries || []);
        props.selectPortfolio_Aside(filters.portfolios || []);
        props.selectDate({
            ...filters.dates,
            startDate: new Date(filters.dates.startDate),
            endDate: new Date(filters.dates.endDate),
        });
    };

    const saveNewFilter = async (name) => {
        try {
            setIsLoading(true);
            const newId = uuidv4();
            const newFilter = {
                id: newId,
                name: name,
                createdTime: moment().valueOf(),
                type: filterType,
                filters: {
                    employees,
                    departments,
                    dates,
                    inquiryTypes,
                    franchises,
                    countries,
                    accounts,
                    industries,
                    portfolios,
                },
            };
            const reqObj = {
                fn: "setUserSettings",
                settingsKey: "sales_filters_settings",
                settingsValue: JSON.stringify([newFilter, ...usersFilters]),
            };
            await post(reqObj);
            const { data } = await get("getLoggedinUser");
            if (data.user) {
                props.setUser(data.user);
            }
            setSelectedFilterId(newId);
        } catch (error) {
            // console.log(error);
        } finally {
            setIsLoading(false);
        }
    };

    const updateSelectedFilter = async (id) => {
        try {
            setIsLoading(true);
            const updatedFilters = usersFilters.map((f) => {
                if (f.id === id) {
                    return {
                        ...f,
                        filters: {
                            employees,
                            departments,
                            dates,
                            inquiryTypes,
                            franchises,
                            countries,
                            accounts,
                            industries,
                            portfolios,
                        },
                    };
                }
                return f;
            });
            const reqObj = {
                fn: "setUserSettings",
                settingsKey: "sales_filters_settings",
                settingsValue: JSON.stringify(updatedFilters),
            };
            await post(reqObj);
            const { data } = await get("getLoggedinUser");
            if (data.user) {
                props.setUser(data.user);
            }
            toast("Filter updated successfully", { type: "success" });
        } catch (error) {
            toast("Something went wrong", { type: "error" });
        } finally {
            setIsLoading(false);
        }
    };

    const deleteSelectedFilter = async () => {
        try {
            setIsLoading(true);
            const updatedArr = usersFilters.filter((item) => item.id !== deletedFilterId);
            const reqObj = {
                fn: "setUserSettings",
                settingsKey: "sales_filters_settings",
                settingsValue: JSON.stringify(updatedArr),
            };
            await post(reqObj);
            const { data } = await get("getLoggedinUser");
            if (data.user) {
                props.setUser(data.user);
            }
            clearAllFilters();
        } catch (error) {
            // console.log(error);
        } finally {
            setIsLoading(false);
        }
    };

    const renameFilter = async (id, newName) => {
        try {
            const updatedFilters = usersFilters.map((f) => {
                if (f.id === id) {
                    return {
                        ...f,
                        name: newName,
                    };
                }
                return f;
            });
            setUsersFilters(updatedFilters);
            setIsLoading(true);
            const reqObj = {
                fn: "setUserSettings",
                settingsKey: "sales_filters_settings",
                settingsValue: JSON.stringify(updatedFilters),
            };
            await post(reqObj);
            const { data } = await get("getLoggedinUser");
            if (data.user) {
                props.setUser(data.user);
            }
            toast("Filter renamed successfully", { type: "success" });
        } catch (error) {
            toast("Something went wrong", { type: "error" });
            // Revert on error
            setUsersFilters(silentlyParseJSON(user.salesFilters, "[]"));
        } finally {
            setIsLoading(false);
        }
    };

    const clearAllFilters = () => {
        setSelectedFilterId(null);

        props.selectEmployee([]);
        props.selectDepartment([]);
        props.selectInquiryType([]);
        props.selectFranchise([]);
        props.selectCountry_Aside([]);
        props.selectAccount_Aside([]);
        props.selectIndustry_Aside([]);
        props.selectPortfolio_Aside([]);
        props.selectDate({
            startDate: moment()
                .startOf("year")
                .toDate(),
            endDate: moment()
                .endOf("year")
                .toDate(),
            predefinedTag: "thisYear",
            dateString: "This Year",
        });
    };

    const isFilterChanged = useMemo(() => {
        const hasFilterChanged = (saved = {}) => {
            const toTimestamp = (value) => {
                if (value === null || value === undefined || value === "") return null;
                const ts = new Date(value).getTime();
                return Number.isNaN(ts) ? null : ts;
            };

            const savedPredefinedTag = saved.dates?.predefinedTag || null;
            const currentPredefinedTag = dates?.predefinedTag || null;

            const datesChanged =
                savedPredefinedTag !== currentPredefinedTag ||
                (savedPredefinedTag === "allTime"
                    ? false
                    : toTimestamp(saved.dates?.startDate) !== toTimestamp(dates?.startDate) ||
                      toTimestamp(saved.dates?.endDate) !== toTimestamp(dates?.endDate));

            return (
                JSON.stringify(saved.employees || []) !== JSON.stringify(employees || []) ||
                JSON.stringify(saved.departments || []) !== JSON.stringify(departments || []) ||
                JSON.stringify(saved.inquiryTypes || []) !== JSON.stringify(inquiryTypes || []) ||
                JSON.stringify(saved.franchises || []) !== JSON.stringify(franchises || []) ||
                JSON.stringify(saved.countries || []) !== JSON.stringify(countries || []) ||
                JSON.stringify(saved.accounts || []) !== JSON.stringify(accounts || []) ||
                JSON.stringify(saved.industries || []) !== JSON.stringify(industries || []) ||
                JSON.stringify(saved.portfolios || []) !== JSON.stringify(portfolios || []) ||
                datesChanged
            );
        };

        if (selectedFilterId) {
            const selectedFilter = usersFilters.find((f) => f.id === selectedFilterId);
            if (!selectedFilter) return true;
            return hasFilterChanged(selectedFilter.filters);
        }

        const hasSameSavedFilter = usersFilters.some((f) => !hasFilterChanged(f.filters));
        return !hasSameSavedFilter;
    }, [
        selectedFilterId,
        usersFilters,
        employees,
        departments,
        dates,
        inquiryTypes,
        franchises,
        countries,
        accounts,
        industries,
        portfolios,
    ]);

    useEffect(() => {
        setUsersFilters(silentlyParseJSON(user.salesFilters, "[]"));
    }, [user.salesFilters]);

    useEffect(() => {
        if (
            (employees && employees.length > 0) ||
            (departments && departments.length > 0) ||
            (inquiryTypes && inquiryTypes.length > 0) ||
            (franchises && franchises.length > 0) ||
            (countries && countries.length > 0) ||
            (accounts && accounts.length > 0) ||
            (industries && industries.length > 0) ||
            (portfolios && portfolios.length > 0) ||
            (dates && dates.predefinedTag !== "thisYear")
        ) {
            setIsFilterApplied(true);
        } else {
            setIsFilterApplied(false);
            setSelectedFilterId(null);
        }
    }, [employees, departments, dates, inquiryTypes, franchises, countries, accounts, industries, portfolios]);

    const prevFilterType = useRef(filterType);

    useEffect(() => {
        if (prevFilterType.current && prevFilterType.current !== filterType) {
            clearAllFilters();
        }
        prevFilterType.current = filterType;
    }, [filterType]);

    const filteredFilters = useMemo(() => {
        return usersFilters.filter((f) => {
            if (f.type === filterType) return true;
            if (!f.type && filterType === "Sales") return true;
            return false;
        });
    }, [usersFilters, filterType]);

    return (
        <>
            {deletedFilterId && (
                <SimpleModal
                    title=""
                    body="Are you sure you want to delete this filter?<br>"
                    className="d-flex flex-column align-items-center"
                    bodyClass="font-weight-semibold font-size-20px"
                    primaryButton="Delete"
                    secondaryButton="Cancel"
                    onClose={(resp) => {
                        if (resp === "OK") deleteSelectedFilter();
                        setDeletedFilterId(null);
                    }}
                />
            )}
            <TooltipOverlay placement="bottom" tooltip="Filters">
                <ButtonWithText
                    label="Open filters"
                    icon={"filter"}
                    buttonStyle="selectedOption"
                    variant="primary"
                    onClick={onToggle}
                ></ButtonWithText>
            </TooltipOverlay>
            <CustomDrawer position="right" size="md" open={isOpen} onClose={onClose}>
                <DrawerContent isLoading={isSalesFullPageLoading(props.salesPageLoading)}>
                    <DrawerHeader onClose={onClose} sx={{ border: "none" }}>
                        <div>Filters</div>
                    </DrawerHeader>
                    <DrawerBody sx={{ paddingTop: 0 }}>
                        <SaveFilter
                            usersFilters={filteredFilters}
                            isFilterApplied={isFilterApplied}
                            selectedFilterId={selectedFilterId}
                            onFilterSelected={onFilterSelected}
                            saveNewFilter={saveNewFilter}
                            updateFilter={updateSelectedFilter}
                            deleteSelectedFilter={(id) => setDeletedFilterId(id)}
                            clearAllFilters={clearAllFilters}
                            isFilterChanged={isFilterChanged}
                            renameFilter={renameFilter}
                            isLoading={isLoading}
                        />
                        {props.employeeFilter && <EmployeeFilter isMultiSelect={true} />}
                        {props.departmentFilter && <DepartmentFilter isMultiSelect={true} />}
                        {props.inquiryTypeFilter && <InquiryTypeFilter isMultiSelect={true} />}
                        {props.franchiseFilter && <FranchiseFilter isMultiSelect={true} />}
                        <DateFilter classname={`${styles.rdrDateRangePickerWrapper} mt-4 mb-8`} drawerLoading={false} />
                    </DrawerBody>
                </DrawerContent>
            </CustomDrawer>
        </>
    );
};

const mapStateToProps = (state) => {
    return {
        user: state.auth.user,
        employees: state.reports.selectedEmployee,
        departments: state.reports.selectedDepartment,
        dates: state.reports.selectedDate,
        inquiryTypes: state.reports.selectedInquiryType,
        franchises: state.reports.selectedFranchise,
        countries: state.reports.selectedCountry_Aside,
        accounts: state.reports.selectedAccount_Aside,
        industries: state.reports.selectedIndustry_Aside,
        portfolios: state.reports.selectedPortfolio_Aside,
        salesPageLoading: state.reports.salesPageLoading,
        employeeFilter: state.app.employeeFilter,
        departmentFilter: state.app.departmentFilter,
        inquiryTypeFilter: state.app.inquiryTypeFilter,
        franchiseFilter: state.app.franchiseFilter,
        filterType: state.app.filterType,
    };
};

const mapDispatchToProps = {
    ...authRedux.actions,
    ...reportsRedux.actions,
};

export default connect(mapStateToProps, mapDispatchToProps)(SalesFilters);
